import {
  type DocumentRepository,
  type DocumentSummary,
  RevisionConflictError,
  type SaveOptions,
  type StoredDocument,
} from '@/application';
import { type SceneDocument } from '@/domain/model';
import { encode, loadDocument } from '@/serialization';

/**
 * A repository over any key-value store.
 *
 * Written against a two-method interface rather than `localStorage` directly, so the same
 * class serves the browser, an in-memory test double and — with a thin wrapper — IndexedDB
 * or a file handle. The alternative, one repository per backend, would duplicate the
 * index bookkeeping and the conflict check three times.
 *
 * `Clock` is injected for the same reason `requestAnimationFrame` is injected into the
 * render scheduler: a timestamp read from a global makes a test either flaky or unable to
 * assert anything about it.
 */

export interface KeyValueStore {
  read(key: string): Promise<string | null>;
  write(key: string, value: string): Promise<void>;
  delete(key: string): Promise<void>;
}

export type Clock = () => number;

const INDEX_KEY = 'edusketch:index';
const documentKey = (id: string): string => `edusketch:doc:${id}`;

interface IndexEntry {
  readonly id: string;
  readonly title: string;
  readonly updatedAt: number;
  readonly revision: string;
}

export interface RepositoryOptions {
  readonly clock?: Clock;
  /** Supplies revision tokens. Injected so tests can make them predictable. */
  readonly nextRevision?: (previous: string | null) => string;
}

export class KeyValueDocumentRepository implements DocumentRepository {
  private readonly store: KeyValueStore;
  private readonly clock: Clock;
  private readonly nextRevision: (previous: string | null) => string;

  constructor(store: KeyValueStore, options: RepositoryOptions = {}) {
    this.store = store;
    this.clock = options.clock ?? (() => Date.now());
    this.nextRevision =
      options.nextRevision ??
      ((previous) => String((previous === null ? 0 : Number.parseInt(previous, 10) || 0) + 1));
  }

  async list(): Promise<readonly DocumentSummary[]> {
    const index = await this.readIndex();
    return [...index.values()]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .map((entry) => ({
        id: entry.id,
        title: entry.title,
        updatedAt: entry.updatedAt,
        revision: entry.revision,
      }));
  }

  async load(id: string): Promise<StoredDocument | null> {
    const text = await this.store.read(documentKey(id));
    if (text === null) return null;

    const parsed: unknown = JSON.parse(text);
    const result = loadDocument(parsed);

    const index = await this.readIndex();
    const entry = index.get(id);

    return {
      summary: {
        id,
        title: result.document.title,
        updatedAt: entry?.updatedAt ?? 0,
        revision: entry?.revision ?? null,
      },
      document: result.document,
      unknown: result.unknown,
      warnings: [...result.warnings, ...result.migrations.map((m) => `migrated: ${m}`)],
    };
  }

  async save(
    id: string,
    document: SceneDocument,
    options: SaveOptions = {}
  ): Promise<DocumentSummary> {
    const index = await this.readIndex();
    const existing = index.get(id);

    // Optimistic concurrency: a stale tab autosaving must not silently overwrite work
    // done in another one.
    if (options.expectedRevision !== undefined) {
      const stored = existing?.revision ?? null;
      if (options.expectedRevision !== stored) {
        throw new RevisionConflictError(stored);
      }
    }

    const wire = encode(document, { preserve: options.unknown });
    await this.store.write(documentKey(id), JSON.stringify(wire));

    const entry: IndexEntry = {
      id,
      title: document.title,
      updatedAt: this.clock(),
      revision: this.nextRevision(existing?.revision ?? null),
    };
    index.set(id, entry);
    await this.writeIndex(index);

    return { id, title: entry.title, updatedAt: entry.updatedAt, revision: entry.revision };
  }

  async remove(id: string): Promise<void> {
    await this.store.delete(documentKey(id));
    const index = await this.readIndex();
    if (index.delete(id)) await this.writeIndex(index);
  }

  /* --------------------------------------------------------------- internals */

  private async readIndex(): Promise<Map<string, IndexEntry>> {
    const text = await this.store.read(INDEX_KEY);
    if (text === null) return new Map();

    try {
      const parsed: unknown = JSON.parse(text);
      if (!Array.isArray(parsed)) return new Map();

      const index = new Map<string, IndexEntry>();
      for (const raw of parsed) {
        if (typeof raw !== 'object' || raw === null) continue;
        const entry = raw as Record<string, unknown>;
        if (typeof entry['id'] !== 'string') continue;
        index.set(entry['id'], {
          id: entry['id'],
          title: typeof entry['title'] === 'string' ? entry['title'] : 'Untitled',
          updatedAt: typeof entry['updatedAt'] === 'number' ? entry['updatedAt'] : 0,
          revision: typeof entry['revision'] === 'string' ? entry['revision'] : '0',
        });
      }
      return index;
    } catch {
      // A corrupt index costs the document *list*, not the documents: each one is stored
      // under its own key and still loads by id. Rebuilding an empty index is far better
      // than refusing to start.
      return new Map();
    }
  }

  private async writeIndex(index: ReadonlyMap<string, IndexEntry>): Promise<void> {
    await this.store.write(INDEX_KEY, JSON.stringify([...index.values()]));
  }
}

/** Wraps `window.localStorage` in the store interface. */
export function localStorageStore(storage: Storage): KeyValueStore {
  return {
    read: (key) => Promise.resolve(storage.getItem(key)),
    write: (key, value) => {
      storage.setItem(key, value);
      return Promise.resolve();
    },
    delete: (key) => {
      storage.removeItem(key);
      return Promise.resolve();
    },
  };
}

/** An in-memory store, for tests and for a session that must not persist. */
export function memoryStore(): KeyValueStore {
  const data = new Map<string, string>();
  return {
    read: (key) => Promise.resolve(data.get(key) ?? null),
    write: (key, value) => {
      data.set(key, value);
      return Promise.resolve();
    },
    delete: (key) => {
      data.delete(key);
      return Promise.resolve();
    },
  };
}
